import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Type } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useBootstrapData, type BootstrapFontSizes } from "@/lib/bootstrap";

type FontSize = "small" | "medium" | "large";

interface FontSizeControlProps {
  variant?: "icon" | "mini" | "menu-row" | "submenu";
  label?: string;
}

const MOBILE_BREAKPOINT = 768;

export default function FontSizeControl({ variant = "icon", label }: FontSizeControlProps) {
  const [fontSize, setFontSize] = useState<FontSize>("medium");
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if we're on mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch font size settings from API
  const { data: fontSizes } = useBootstrapData<BootstrapFontSizes | undefined>((data) => data.layout.fontSizes);

  // Apply font size values to CSS custom properties based on screen size
  useEffect(() => {
    if (fontSizes) {
      const root = document.documentElement;
      if (isMobile) {
        root.style.setProperty('--font-scale-small', fontSizes.mobileSmall.toString());
        root.style.setProperty('--font-scale-medium', fontSizes.mobileMedium.toString());
        root.style.setProperty('--font-scale-large', fontSizes.mobileLarge.toString());
      } else {
        root.style.setProperty('--font-scale-small', fontSizes.small.toString());
        root.style.setProperty('--font-scale-medium', fontSizes.medium.toString());
        root.style.setProperty('--font-scale-large', fontSizes.large.toString());
      }
    }
  }, [fontSizes, isMobile]);

  // Load font size preference from localStorage on mount, or use admin default
  useEffect(() => {
    const stored = localStorage.getItem("fontSize") as FontSize | null;
    if (stored && ["small", "medium", "large"].includes(stored)) {
      setFontSize(stored);
      document.documentElement.setAttribute("data-font-size", stored);
    } else if (fontSizes?.defaultSize && ["small", "medium", "large"].includes(fontSizes.defaultSize)) {
      // No user preference saved, use admin-configured default
      setFontSize(fontSizes.defaultSize);
      document.documentElement.setAttribute("data-font-size", fontSizes.defaultSize);
    } else {
      document.documentElement.setAttribute("data-font-size", "medium");
    }
  }, [fontSizes?.defaultSize]);

  const handleFontSizeChange = (size: FontSize) => {
    setFontSize(size);
    localStorage.setItem("fontSize", size);
    document.documentElement.setAttribute("data-font-size", size);
    setIsOpen(false);
  };

  // Submenu variant for use inside DropdownMenu
  if (variant === "submenu") {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="button-font-size-submenu">
          <Type className="h-4 w-4 mr-2" />
          Font Size
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuRadioGroup value={fontSize} onValueChange={(v) => handleFontSizeChange(v as FontSize)}>
            <DropdownMenuRadioItem value="small" data-testid="button-font-small">
              Small
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="medium" data-testid="button-font-medium">
              Medium
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="large" data-testid="button-font-large">
              Large
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  const popoverContent = (
    <PopoverContent className="w-auto p-2" align="end">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-medium text-muted-foreground px-2 py-1">
          Text size
        </div>
        <Button
          variant={fontSize === "small" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => handleFontSizeChange("small")}
          className="justify-start"
          data-testid="button-font-small"
        >
          <span className="text-sm">A</span>
          <span className="ml-2">Small</span>
        </Button>
        <Button
          variant={fontSize === "medium" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => handleFontSizeChange("medium")}
          className="justify-start"
          data-testid="button-font-medium"
        >
          <span className="text-base">A</span>
          <span className="ml-2">Medium</span>
        </Button>
        <Button
          variant={fontSize === "large" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => handleFontSizeChange("large")}
          className="justify-start"
          data-testid="button-font-large"
        >
          <span className="text-lg">A</span>
          <span className="ml-2">Large</span>
        </Button>
      </div>
    </PopoverContent>
  );

  // Mini variant for home page/header
  if (variant === "mini") {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-11 w-11 p-0 rounded-sm border-2 transition-all duration-100 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none bg-background"
            style={{
              borderColor: 'var(--brutal-border-color)',
              boxShadow: 'var(--brutal-shadow-sm)',
            }}
            data-testid="button-font-size-mini"
            aria-label="Adjust font size"
          >
            <Type className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        {popoverContent}
      </Popover>
    );
  }

  // Legacy menu-row variant (kept for backward compatibility but use submenu instead)
  if (variant === "menu-row") {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div 
            className="w-full flex items-center justify-between px-2 py-1.5 cursor-pointer"
            data-testid="button-font-size-menu"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <span className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              Font Size
            </span>
            <Type className="h-4 w-4 text-muted-foreground" />
          </div>
        </PopoverTrigger>
        {popoverContent}
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={label ? "default" : "icon"}
          className={label ? "gap-2" : ""}
          data-testid="button-font-size"
          aria-label="Adjust text size"
        >
          <Type className="h-5 w-5" />
          {label && <span className="hidden sm:inline font-medium">{label}</span>}
        </Button>
      </PopoverTrigger>
      {popoverContent}
    </Popover>
  );
}
